import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { OfferSelectionCardModel } from '@flocasts/experience-service-types'
import { combineLatest, filter, Observable, of } from 'rxjs'
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { LOGGER_SERVICE } from 'src/app/logger/logger.config'
import { LoggerService } from 'src/app/logger/logger.interface'
import { EnvironmentService } from 'src/app/singleton-services/environment.service'
import * as PlansUtility from '../funnel/plans/legacy-plans/legacy-plans.utility'
import { GiftRedeemed, PaymentMade, SegmentEvents } from '../shared/analytics/models/segment-events.model'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { CouponService } from '../shared/services/coupon.service'
import { AuthService } from './auth/auth.service'
import { notNullOrUndefined } from '../shared/functions/type-guards'
import { SubscriptionModel } from '../change-offer/subscription-offerings.interfaces'
import {
  ClaimGiftSubscriptionResponse,
  CreateSubscriptionResponse,
  OfferAnalyticsExtraContext,
  PaymentIntentResponse,
  PendingSubscriptionResponse,
  PreviewSubscriptionResponse
} from '../funnel/pay/payments.interfaces'
import { PaymentResponseModel, StripeSubscriptionStatus } from '@flocasts/payments'
import { MinimalSubscription, MinimalSubscriptionResponse } from '../shared/subscriptions/subscriptions.interface'
import { isAfter } from 'date-fns'
import { VerticalService } from './vertical.service'

@Injectable({
  providedIn: 'root'
})
/**
 * Docs: {@link https://payments.stag-aws.flokubernetes.com/docs#/default/UsersController_getUserSubscriptions}
 */
export class PaymentsService {
  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly authService: AuthService,
    private readonly couponService: CouponService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly segmentService: SegmentService,
    private readonly verticalService: VerticalService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  public previewSubscription(subscription: SubscriptionModel, offer: OfferSelectionCardModel) {
    const url = `${this.environmentService.config.endpoints.payments}/subscriptions/${subscription.id}/preview`
    return this.http
      .post<PreviewSubscriptionResponse | undefined>(url, {
        offeringIds: [offer.id]
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error accessing Payments Service', { error })
          return of(undefined)
        })
      )
  }

  public createPaymentIntent(
    offer: OfferSelectionCardModel,
    captchaToken: string,
    coupon?: string,
    email?: string
  ): Observable<PaymentIntentResponse | undefined> {
    const url = `${this.environmentService.config.endpoints.payments}/payment-intents`
    return this.http
      .post<PaymentIntentResponse>(url, {
        offer: offer?.id,
        captcha: captchaToken,
        coupon,
        email
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 403 && error.status !== 401) {
            this.logger.error('Error creating payment intent', { error })
          }
          throw new Error(error.message)
        })
      )
  }

  public claimGiftSubscription(
    redeemCode: string,
    captcha: string,
    offer: OfferSelectionCardModel
  ): Observable<ClaimGiftSubscriptionResponse | undefined> {
    const url = `${this.environmentService.config.endpoints.payments}/gift-subscription/${redeemCode}/claim`
    const body = {
      captcha
    }
    return this.http.post<ClaimGiftSubscriptionResponse>(url, body).pipe(
      tap(() => {
        const paymentLevel = () => {
          if (offer?.offerMetadata?.period === 'year') return 'yearly'
          if (offer?.offerMetadata?.period === 'month') return 'monthly'
          return ''
        }

        const analyticProps: GiftRedeemed = {
          name: SegmentEvents.GIFT_REDEEMED,
          properties: {
            payment_level: paymentLevel(),
            is_gifter: false
          }
        }
        this.segmentService.trackEvent(analyticProps, true)
      }),
      catchError(err => {
        this.logger.error('An error occurred when claiming a gifted subscription', err)
        return of(undefined)
      })
    )
  }

  public createGiftSubscription(
    offer: OfferSelectionCardModel,
    paymentIntentId: string,
    email: string,
    captchaToken: string
  ): Observable<PendingSubscriptionResponse | undefined> {
    const url = `${this.environmentService.config.endpoints.payments}/gift-subscription`
    const body = {
      type: 'stripe',
      offering: offer.id,
      email,
      currency: offer?.offerMetadata?.currency,
      paymentIntent: paymentIntentId,
      captcha: captchaToken
    }
    return this.http.post<PendingSubscriptionResponse>(url, body).pipe(
      tap(() => {
        // creating a new object here in order to leverage types because
        // the extraContext prop on AnalyticsModel is of type 'any'
        const offerAnalyticsProps: OfferAnalyticsExtraContext = {
          currency: offer?.offerMetadata?.currency,
          amount: offer?.analytics?.extraContext?.amount
        }

        const paymentLevel = () => {
          if (offer?.offerMetadata?.period === 'year') return 'yearly'
          if (offer?.offerMetadata?.period === 'month') return 'monthly'
          return ''
        }

        this.onPaymentMadeSegmentCall(
          offerAnalyticsProps.amount,
          offerAnalyticsProps.amount ? PlansUtility.setPriceToCurrencyFormat(offerAnalyticsProps.amount) : undefined,
          offerAnalyticsProps.currency,
          'Stripe',
          this.activatedRoute.snapshot.queryParamMap.has('embeddedPurchase') ? 'Android' : 'Web',
          paymentLevel(),
          true
        )
      }),
      catchError((err: HttpErrorResponse) => {
        if (err && err.message) {
          this.logger.error('Payment failure', {
            error: err
          })
        }
        // 400 error is typically a recaptcha error,
        // @see {@link https://github.com/flocasts/payments/blob/master/src/subscription/subscription.controller.ts#L57}
        // Not throwing error here to preserve existing logic around recaptcha errors
        if (err instanceof HttpErrorResponse && err.status === 400) {
          if (err.error.errors.message) {
            return of(err.error.errors.message)
          }
          return of({
            message:
              'An error occurred while processing your payment. You were not charged. Please try again or contact support.'
          })
        }
        // default error case is 500,
        // @see {@link https://github.com/flocasts/payments/blob/master/src/core/exception-filters/all-exceptions.filter.ts#L55
        // Throw error here to handle error messaging in component
        if (err instanceof HttpErrorResponse && err.status === 500) {
          const error500 = {
            ...err,
            message:
              'An error occurred while processing your payment. You were not charged. Please try again or contact support.'
          }
          throw error500
        }
        return of(undefined)
      })
    )
  }

  public createSubscription(
    offer: OfferSelectionCardModel | undefined,
    paymentIntentId: string | undefined,
    captchaToken: string
  ): Observable<CreateSubscriptionResponse | undefined> {
    const user = this.authService.getUserFromStoredToken()
    return combineLatest([this.couponService.coupon$, this.verticalService.siteId$]).pipe(
      switchMap(([coupon, siteId]) => {
        const url = `${this.environmentService.config.endpoints.payments}/subscriptions`
        const body = {
          type: 'stripe',
          offering: offer?.id,
          user: Number(user?.id),
          coupon: !!coupon ? coupon.id : '',
          currency: offer?.offerMetadata?.currency,
          paymentIntent: paymentIntentId,
          captcha: captchaToken,
          extraMetadata: {
            siteId
          }
        }
        return this.http.post<CreateSubscriptionResponse>(url, body)
      }),
      tap(() => {
        /**
         * After a user successfully purchases a sub in the new payments architecture,
         * refresh the client side user token with its current value to refetch site
         * entitlements for that user in the auth service.
         */
        this.authService.userIdentitySource.next(user)
        this.couponService.clearCoupon()
      }),
      tap(() => {
        // creating a new object here in order to leverage types because
        // the extraContext prop on AnalyticsModel is of type 'any'
        const offerAnalyticsProps: OfferAnalyticsExtraContext = {
          currency: offer?.offerMetadata?.currency,
          amount: offer?.analytics?.extraContext?.amount
        }

        const paymentLevel = () => {
          if (offer?.offerMetadata?.isInstallment) return 'installment'
          if (offer?.offerMetadata?.period === 'year') return 'yearly'
          if (offer?.offerMetadata?.period === 'month') return 'monthly'
          return ''
        }

        this.onPaymentMadeSegmentCall(
          offerAnalyticsProps.amount,
          offerAnalyticsProps.amount ? PlansUtility.setPriceToCurrencyFormat(offerAnalyticsProps.amount) : undefined,
          offerAnalyticsProps.currency,
          'Stripe',
          this.activatedRoute.snapshot.queryParamMap.has('embeddedPurchase') ? 'Android' : 'Web',
          paymentLevel()
        )
      }),
      catchError((err: any) => {
        if (err && err.message) {
          this.logger.error(err.message, err.error)
        }
        // 400 error is typically a recaptcha error,
        // @see {@link https://github.com/flocasts/payments/blob/master/src/subscription/subscription.controller.ts#L57}
        // Not throwing error here to preserve existing logic around recaptcha errors
        if (err instanceof HttpErrorResponse && err.status === 400) {
          return of(err.error.errors.message)
        }
        // default error case is 500,
        // @see {@link https://github.com/flocasts/payments/blob/master/src/core/exception-filters/all-exceptions.filter.ts#L55
        // Throw error here to handle error messaging in component
        if (err instanceof HttpErrorResponse) {
          if (err.status === 500) {
            throw {
              ...err,
              message:
                'An error occurred while processing your payment. You were not charged. Please try again or contact support.'
            }
          }
          // Payment failed errors; propagated from Stripe
          let stripeError = err.error.message
          // We don't want to tell the customer to contact their bank if Stripe already did or if they provided a reason
          if (!stripeError.includes('contact your bank') && !stripeError.includes('insufficient funds')) {
            stripeError = stripeError.concat(
              ' If you are certain you have entered the correct information for an unexpired card, please reach out to your bank or contact support.'
            )
          }
          throw {
            ...err,
            message: stripeError
          }
        }

        return of(undefined)
      })
    )
  }

  onPaymentMadeSegmentCall(
    paymentAmount: number | undefined,
    revenue: string | undefined,
    currency: string | undefined,
    paymentProcessor: 'Stripe',
    platform: 'Android' | 'Web',
    paymentLevel: string,
    isGifter?: boolean
  ) {
    const analyticProps: PaymentMade = {
      name: SegmentEvents.PAYMENT_MADE,
      properties: {
        payment_amount: paymentAmount,
        revenue,
        currency,
        payment_processor: paymentProcessor,
        platform,
        payment_level: paymentLevel,
        universal: true,
        is_gifter: isGifter
      }
    }
    // Need to pass true as second arg, for TUNE HasOffers Postback
    this.segmentService.trackEvent(analyticProps, true)
  }

  public getUserSubscriptions(active: boolean, userId: string): Observable<SubscriptionModel[] | undefined> {
    const url = `${this.environmentService.config.endpoints.payments}/users/${userId}/subscriptions`
    return this.http
      .get<SubscriptionModel[]>(url, {
        params: {
          onlyActive: active
        }
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error fetching user active subscriptions', { error })
          return of(undefined)
        })
      )
  }

  public upgradeSubscription(subscription: SubscriptionModel, offer: OfferSelectionCardModel) {
    const url = `${this.environmentService.config.endpoints.payments}/subscriptions/${subscription.id}`
    const user = this.authService.getUserFromStoredToken()
    return this.http
      .post<PreviewSubscriptionResponse | undefined>(url, {
        offeringIds: [offer.id]
      })
      .pipe(
        tap(() => {
          /**
           * After a user successfully changes their sub in the new payments architecture,
           * refresh the client side user token with its current value to refetch site
           * entitlements for that user in the auth service.
           */
          this.authService.userIdentitySource.next(user)
        }),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error accessing Payments Service', { error })
          return of(undefined)
        })
      )
  }

  public activeOffering$ = this.authService.userIdentity$.pipe(
    switchMap(user => {
      if (!user) return of(null)
      return this.getUserSubscriptions(false, user.id).pipe(
        filter(notNullOrUndefined),
        map(subscriptions => {
          if (subscriptions && subscriptions[0]) {
            if (subscriptions[0].status === StripeSubscriptionStatus.Active && subscriptions[0].offers) {
              return subscriptions[0].offers[0]
            } else if (
              subscriptions[0].status === StripeSubscriptionStatus.Cancelled &&
              isAfter(new Date(subscriptions[0].accessEndDate), Date.now())
            ) {
              return subscriptions[0].offers[0]
            }
          }
          return undefined
        }),
        shareReplay(1)
      )
    })
  )

  public activeSubscription$ = this.authService.userIdentity$.pipe(
    switchMap(user => {
      if (!user) return of(null)
      return this.getUserSubscriptions(true, user.id).pipe(
        filter(notNullOrUndefined),
        map(subscriptions => subscriptions[0]),
        shareReplay(1)
      )
    })
  )

  public cancelSubscription(subscriptionId: string) {
    return this.http
      .delete<PaymentResponseModel[]>(
        `${this.environmentService.config.endpoints.payments}/subscriptions/${subscriptionId}`
      )
      .pipe(
        catchError(err => {
          // 404 error is expected if user has no subscriptions
          if (err.status !== 404) this.logger.error(`Error cancelling subscriptionId: ${subscriptionId}`, err)
          return of([])
        })
      )
  }

  /**
   * Calls the payment service's updateSubscription endpoint with special action param `spac`. We enable
   * the 20% discount by using the obfuscated special action param. The param will not be in the Swagger docs.
   */
  public applyCoupon(
    subscriptionId: string,
    offeringId: string,
    specialAction: string
  ): Observable<MinimalSubscription | string> {
    return this.http
      .post<string>(
        `${this.environmentService.config.endpoints.payments}/subscriptions/${subscriptionId}`,
        {
          offeringIds: [offeringId]
        },
        {
          params: {
            spac: specialAction
          }
        }
      )
      .pipe(
        catchError(err => {
          // 404 error is expected if user has no subscriptions
          if (err.status !== 404) {
            this.logger.error(`Error applying coupon for churn prevention. subscriptionId: ${subscriptionId}`, err)
          }
          return of('There was an error with the server')
        })
      )
  }

  public reactivateSubscription(subscriptionId: string): Observable<MinimalSubscriptionResponse> {
    const url = `${this.environmentService.config.endpoints.payments}/subscriptions/${subscriptionId}/revert-cancellation`
    return this.http.patch<MinimalSubscription>(url, {}).pipe(
      catchError((err: any) => {
        const error = err.error
        const message = (error && error.message) || 'There was an error with the server.'

        return of({ error, message })
      })
    )
  }

  public updateCard(token: string) {
    return this.http
      .patch<PaymentResponseModel[]>(`${this.environmentService.config.endpoints.payments}/stripe/card`, {
        token
      })
      .pipe(
        map(() => void 0),
        catchError(err => {
          this.logger.error(`Error updating credit card. Stripe token: ${token}`, err)
          throw err
        })
      )
  }
}
